/**
 * Pure crossfade policy — shared by every player surface.
 *
 * The *how* of crossfading is surface-specific (web wires dual HTMLAudioElements
 * through Web Audio GainNodes in `audio-mixer.ts`; the native shells drive two
 * expo-audio players). The *whether*, *how long*, and *the gain ramp* are pure
 * decisions with no DOM or platform dependency, so they live here in
 * `@workspace/player-core` and every surface reads the same rules. That is what
 * makes DJ-mode crossfade behave identically on web, desktop, TV, and mobile
 * instead of being a web-only feature.
 */

export type CrossfadeMode = "standard" | "crossfade";

/**
 * Resolve the effective crossfade mode from a device's settings blob. An
 * explicit `audioPlaybackMode` wins; otherwise the legacy `djMode` toggle maps
 * to crossfade (DJ Mode == "smooth transitions on the music channel").
 */
export function readCrossfadeMode(settings: unknown): CrossfadeMode {
  const s = settings as { audioPlaybackMode?: unknown; djMode?: unknown } | null;
  if (s?.audioPlaybackMode === "crossfade") return "crossfade";
  if (s?.audioPlaybackMode === "standard") return "standard";
  return s?.djMode ? "crossfade" : "standard";
}

/** Crossfade duration in seconds, clamped to the supported 3–8s range (default 5s). */
export function readCrossfadeDurationSec(settings: unknown): number {
  const raw = (settings as { crossfadeDurationSec?: unknown } | null)
    ?.crossfadeDurationSec;
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return 5;
  return Math.max(3, Math.min(8, n));
}

/**
 * Crossfade guard (CB-085): only a music→music transition that is NOT an
 * interrupt crossfades, and only when a mixer exists and crossfade mode is on.
 * Ad/announcement/video transitions and interrupt cut-ins hard-cut so spots and
 * takeovers land cleanly. Pure so the decision is testable without a player.
 *
 * CB-142: an operator-initiated skip (next/previous/shuffle) also hard-cuts,
 * even in the middle of a music→music transition. A person pressing "next"
 * expects the new track NOW, not a 3–8 s fade — a crossfade there feels like the
 * command was ignored. `manual` is the caller's "this was a human transport
 * action" signal; only automatic rotation (a track ending into the next one)
 * still crossfades.
 */
export function shouldCrossfadeTransition(opts: {
  hasMixer: boolean;
  mode: CrossfadeMode;
  prevReason: string | null | undefined;
  nextReason: string | null | undefined;
  interrupt: boolean;
  manual?: boolean;
}): boolean {
  return (
    opts.hasMixer &&
    opts.mode === "crossfade" &&
    opts.prevReason === "music" &&
    opts.nextReason === "music" &&
    !opts.interrupt &&
    !opts.manual
  );
}

/**
 * Compute complementary gain ramps for a crossfade: `from` fades 1→0 and `to`
 * fades 0→1 over `ceil(durationSec*1000/stepMs)` steps (min 2). At every index
 * `from[i] + to[i] === 1`, so the perceived level stays constant across the
 * fade. Surfaces apply these to whatever gain primitive they have (Web Audio
 * GainNode on web, per-player volume on native).
 */
export function crossfadeGainSteps(
  durationSec: number,
  stepMs = 50,
): { from: number[]; to: number[] } {
  const steps = Math.max(2, Math.ceil((durationSec * 1000) / stepMs));
  const from: number[] = [];
  const to: number[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    from.push(1 - t);
    to.push(t);
  }
  return { from, to };
}
